import React from "react";
import { useTheme } from "@glossy";
import { Dataviz, DatavizName } from "@radarly/models/dataviz";
import AI from "./_ai";
import BubbleChart from "./_bubble-chart";
import Cloud from "./_cloud";
import Demographic from "./_demographic";
import DoubleTimeChart from "./_double-time-chart";
import DoughnutChart from "./_doughnut-chart";
import Embedded from "./_embedded";
import Emv from "./_emv";
import HorizontalBarChart from "./_horizontal-bar-chart";
import HorizontalStackedBarChart from "./_horizontal-stacked-bar-chart";
import ImageClustering from "./_image-clustering";
import Kpi from "./_kpi";
import LineChart from "./_line-chart";
import Map from "./_map";
import MultipleLineChart from "./_multiple-line-chart";
import PictureWall from "./_picture-wall";
import PinnedAuthor from "./_pinned-author";
import PinnedPost from "./_pinned-post";
import StackedBarChart from "./_stacked-bar-chart";
import StreamChart from "./_stream-chart";
import Sunburst from "./_sunburst";
import Text from "./_text";
import TopAuthors from "./_top-authors";
import TopPosts from "./_top-posts";
import Treemap from "./_treemap";

type Color = string;

const BASE_COLOR = "#8593AE";

export const PRESETS = {
  default: ["#BE45B0", "#FA4E7C", "#FF7826", "#F29F0C", "#0887C9"],
  platform: ["#3B5998", "#1DA1F2", "#0BC082", "#9DCE0D", "#DFA543"],
  tone: ["#1FAA7A", "#B4B1BA", "#6666FF", "#E64C65"],
  gender: ["#FF0099", "#0099FF"],
  affect: ["#1FAA7A", "#E64C65"],
};

type Preset = keyof typeof PRESETS;

type illustrationOptions = {
  showPrevious?: boolean;
  towns?: boolean;
  regions?: boolean;
  emojis?: boolean;
  showLine?: boolean;
  scatterPlot?: boolean;
  normalized?: boolean;
};

export interface IProps {
  datavizName: Dataviz["name"];
  colorScheme?: Preset | Color; // Can be either the name of a preset, a custom color, or nothing (takes base color)
  options?: illustrationOptions;
}

export type Colors = {
  base: Color;
  content: {
    primary: Color;
    secondary: Color;
    tertiary: Color;
  };
  chart: {
    primary: Color;
    secondary: Color;
  };
  scheme: Color[];
};

export interface IllustrationProps {
  colors: Colors;
  options?: IProps["options"];
}

const getSVG = (name: Dataviz["name"]): ((IllustrationProps) => JSX.Element) =>
  ({
    [DatavizName.BubbleChart]: BubbleChart,
    [DatavizName.Cloud]: Cloud,
    [DatavizName.Demographic]: Demographic,
    [DatavizName.DoubleTimeChart]: DoubleTimeChart,
    [DatavizName.Embedded]: Embedded,
    [DatavizName.HorizontalBarChart]: HorizontalBarChart,
    [DatavizName.HorizontalStackedBarChart]: HorizontalStackedBarChart,
    [DatavizName.Kpi]: Kpi,
    [DatavizName.LineChart]: LineChart,
    [DatavizName.Map]: Map,
    [DatavizName.MultipleLineChart]: MultipleLineChart,
    [DatavizName.PictureWall]: PictureWall,
    [DatavizName.DoughnutChart]: DoughnutChart,
    [DatavizName.ScatterPlot]: BubbleChart,
    [DatavizName.StackedBarChart]: StackedBarChart,
    [DatavizName.StreamChart]: StreamChart,
    [DatavizName.Sunburst]: Sunburst,
    [DatavizName.Text]: Text,
    [DatavizName.TopAuthors]: TopAuthors,
    [DatavizName.TopPosts]: TopPosts,
    [DatavizName.TreeMap]: Treemap,
    [DatavizName.PinnedPost]: PinnedPost,
    [DatavizName.PinnedAuthor]: PinnedAuthor,
    [DatavizName.ImageClustering]: ImageClustering,
    [DatavizName.Emv]: Emv,
    [DatavizName.AI]: AI,
  }[name]);

export const getColors = (colorScheme?: IProps["colorScheme"]) =>
  PRESETS[colorScheme] || (colorScheme ? [colorScheme] : []);

const RadarlyCardEditionIllustration = ({
  datavizName,
  colorScheme,
  options = {},
}: IProps) => {
  const { content, chart } = useTheme();
  const svg = getSVG(datavizName);
  return (
    svg &&
    React.createElement(svg, {
      options,
      colors: {
        base: BASE_COLOR,
        content,
        chart,
        scheme: getColors(colorScheme),
      },
    })
  );
};

export default RadarlyCardEditionIllustration;
